/**
 * TikTok publishing copy — a domain slice of the English catalog.
 *
 * Split out of en.ts rather than appended to it: en.ts sits on the 500-line
 * ceiling enforced by the fileSize ratchet (src/__tests__/fileSize.test.ts),
 * and several branches append to it concurrently, so whichever one lands last
 * tips it over. Keys live here and are spread into `en`, so `MessageKey`
 * (keyof typeof en) still resolves them and every t('tiktok.*') call site is
 * unchanged.
 *
 * Add new TikTok copy here, not in en.ts.
 */
export const enTiktok = {
  // Shown when polling ends with no terminal status from TikTok. Deliberately
  // not phrased as success: the publish may still fail, and the composer keeps
  // the user's draft so they can retry.
  'tiktok.publishTimeout':
    'Still processing on TikTok’s side. We stopped checking after 2 minutes — check your TikTok profile or notifications to confirm.',
  'tiktok.publishTimeoutHint':
    'Your caption and media have been kept here so you can retry if it did not go through.',
  'tiktok.publishTimeoutToast':
    'TikTok is still processing. Check your TikTok profile to confirm it published.',
  // While TikTok has not yet answered, nothing has been "submitted": the
  // banner says so rather than promising a post that may still be refused.
  'tiktok.inFlightHint':
    'Not accepted by TikTok yet — this banner updates as soon as TikTok answers.',
  'tiktok.sentToInboxHint':
    'Nothing is published yet: open the TikTok app, find it in your inbox notifications, and post it from there.',
  'tiktok.directPostHint':
    'Published as a Direct Post. Check your TikTok profile to view the video.',
  'tiktok.scopeUnapproved':
    'TikTok rejected the requested scopes (invalid_scope). In the TikTok Developer Portal, add and approve user.info.basic, video.publish, video.upload, user.info.profile, and user.info.stats for this app, then reconnect.',
  'tiktok.scopeUserInfoBasic':
    'View your TikTok profile username and avatar',
  'tiktok.scopeVideoPublish':
    'Publish videos directly to your TikTok feed with the settings you choose (Content Posting API — Direct Post)',
  'tiktok.scopeVideoUpload':
    'Save videos to your TikTok drafts for editing in the app before publishing (Content Posting API — Upload to Inbox)',
  'tiktok.scopeUserInfoProfile':
    'Read your public TikTok profile details',
  'tiktok.scopeUserInfoStats':
    'Read your public TikTok profile statistics',
} as const;
